import { createReducer, on } from '@ngrx/store';

import { isNil } from 'lodash-es';
import { round } from 'lodash-es';

import { AmortizationReportData } from '../../typings/calculator';
import * as fromCalculatorActions from '../actions/calculator.actions';

export const calculatorFeatureKey = 'calculator';

export interface State {
  affordabilityBuyerName: string | null;
  affordabilityDownPayment: number;
  affordabilityInterestRate: number;
  affordabilityMonthlyHomeownersInsurance: number;
  affordabilityMonthlyPayment: number;
  affordabilityMortgageAmount: number;
  affordabilityMortgageTerm: number;
  affordabilityPrincipalAndInterest: number;
  affordabilityPrivateMortgageInsurance: number;
  affordabilityPropertyTax: number;
  affordabilityPurchasePrice: number;
  amortizationAmount: number;
  amortizationInterestRate: number;
  amortizationMortgageTerm: number;
  amortizationReportData: AmortizationReportData | null;
  pitiBuyerName: string | null;
  pitiDownPayment: number;
  pitiInterestRate: number;
  pitiMonthlyHomeownersInsurance: number;
  pitiMonthlyPayment: number;
  pitiMortgageAmount: number;
  pitiMortgageTerm: number;
  pitiPrincipalAndInterest: number;
  pitiPrivateMortgageInsurance: number;
  pitiPropertyTax: number;
  pitiPurchasePrice: number;
}

export const initialState: State = {
  affordabilityBuyerName: null,
  affordabilityDownPayment: 0,
  affordabilityInterestRate: 0,
  affordabilityMonthlyHomeownersInsurance: 0,
  affordabilityMonthlyPayment: 0,
  affordabilityMortgageAmount: 0,
  affordabilityMortgageTerm: 30,
  affordabilityPrincipalAndInterest: 0,
  affordabilityPrivateMortgageInsurance: 0,
  affordabilityPropertyTax: 0,
  affordabilityPurchasePrice: 0,
  amortizationAmount: 0,
  amortizationInterestRate: 0,
  amortizationMortgageTerm: 30,
  amortizationReportData: null,
  pitiBuyerName: null,
  pitiDownPayment: 0,
  pitiInterestRate: 0,
  pitiMonthlyHomeownersInsurance: 0,
  pitiMonthlyPayment: 0,
  pitiMortgageAmount: 0,
  pitiMortgageTerm: 30,
  pitiPrincipalAndInterest: 0,
  pitiPrivateMortgageInsurance: 0,
  pitiPropertyTax: 0,
  pitiPurchasePrice: 0
};

export const reducer = createReducer(
  initialState,
  on(fromCalculatorActions.updatePITIMortgageTerm, (state, { payload: { mortgageTerm } }) => ({
    ...state,
    pitiMortgageTerm: mortgageTerm
  })),
  on(fromCalculatorActions.updatePITICalculatorData, (state, { payload }) => {
    let downPayment = 0;
    let mortgageAmount = 0;
    if (!isNil(payload.downPayments) && !isNil(payload.homePrice)) {
      downPayment =
        payload.downPayments < 100 ? payload.homePrice * (payload.downPayments / 100) : payload.downPayments;
      mortgageAmount = payload.homePrice - downPayment;
    }

    let propertyTax = 0;
    if (!isNil(downPayment) && !isNil(payload.homePrice)) {
      const annualPropertyTaxes =
        payload.annualPropertyTaxes < 100
          ? (payload.annualPropertyTaxes / 100) * payload.homePrice
          : payload.annualPropertyTaxes;
      propertyTax = round(annualPropertyTaxes / 12 || 0, 2);
    }

    let monthlyHomeownersInsurance = 0;
    if (!isNil(payload.annualOwnersInsurance)) {
      monthlyHomeownersInsurance = round(payload.annualOwnersInsurance / 12 || 0, 2);
    }

    let privateMortgageInsurance = 0;
    if (!isNil(downPayment) && !isNil(mortgageAmount) && !isNil(payload.homePrice)) {
      privateMortgageInsurance = 0;

      if (downPayment < payload.homePrice * 0.2) {
        const percentCalculated = mortgageAmount / payload.homePrice;
        const percentOfMaxLTV = ((percentCalculated - 0.8) * 100) / 20;
        const pmiRate = Math.max(0.3, 1.15 * percentOfMaxLTV) / 100;
        const pmiAnnual = mortgageAmount * pmiRate;

        privateMortgageInsurance = round(pmiAnnual / 12, 2);
      }
    }

    let principalAndInterest = 0;
    if (!isNil(payload.interestRate) && !isNil(mortgageAmount)) {
      const monthlyInterestRate = payload.interestRate / 100 / 12;
      const interestPowerNum = Math.pow(1 + monthlyInterestRate, state.pitiMortgageTerm * 12);
      const factor1 = monthlyInterestRate * interestPowerNum;
      const factor2 = interestPowerNum - 1;

      principalAndInterest = round((mortgageAmount * factor1) / factor2, 2);
    }

    let monthlyPayment = 0;
    if (
      !isNil(principalAndInterest) &&
      !isNil(propertyTax) &&
      !isNil(monthlyHomeownersInsurance) &&
      !isNil(privateMortgageInsurance)
    ) {
      monthlyPayment = principalAndInterest + propertyTax + monthlyHomeownersInsurance + privateMortgageInsurance;
    }

    return {
      ...state,
      pitiBuyerName: payload.buyerName || '',
      pitiDownPayment: downPayment,
      pitiInterestRate: payload.interestRate,
      pitiMonthlyHomeownersInsurance: monthlyHomeownersInsurance,
      pitiMonthlyPayment: Math.abs(monthlyPayment),
      pitiMortgageAmount: Math.abs(mortgageAmount),
      pitiPrincipalAndInterest: Math.abs(principalAndInterest),
      pitiPrivateMortgageInsurance: privateMortgageInsurance,
      pitiPropertyTax: propertyTax,
      pitiPurchasePrice: payload.homePrice
    };
  }),
  on(fromCalculatorActions.updateAffordabilityMortgageTerm, (state, { payload: { mortgageTerm } }) => ({
    ...state,
    affordabilityMortgageTerm: mortgageTerm
  })),
  on(fromCalculatorActions.updateAffordabilityCalculatorData, (state, { payload }) => {
    const numberOfPayments = state.affordabilityMortgageTerm * 12;

    let downPayment = 0;
    let monthlyInterestRate = 0;
    let totalAmount = 0;
    if (!isNil(payload.downPayments) && !isNil(payload.monthlyPayments)) {
      monthlyInterestRate = payload.interestRate / 100 / 12;
      totalAmount =
        (payload.monthlyPayments / monthlyInterestRate) * (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments));
      downPayment = payload.downPayments < 100 ? totalAmount * (payload.downPayments / 100) : payload.downPayments;
    }

    let totalEstimatedAmount = 0;
    let monthlyTaxes = 0;
    let monthlyInsurance = 0;
    if (
      !isNil(payload.annualOwnersInsurance) &&
      !isNil(payload.monthlyPayments) &&
      !isNil(payload.annualPropertyTaxes)
    ) {
      const adjustedTaxRate = downPayment / totalAmount;
      const tmpPmt = payload.monthlyPayments * (0.61 + adjustedTaxRate);

      totalEstimatedAmount = round(
        (tmpPmt / monthlyInterestRate) * (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments)),
        2
      );

      let annualTaxRate = 1.3 / 100.0;
      let annualTaxes = totalEstimatedAmount * annualTaxRate;

      if (payload.annualPropertyTaxes === 0) {
        // use the default tax rate because we didn't get anything
        annualTaxRate = 0.0;
        annualTaxes = totalEstimatedAmount * annualTaxRate;
      } else if (payload.annualPropertyTaxes < 100) {
        // property tax < 100 means it is some other percentage
        annualTaxRate = payload.annualPropertyTaxes / 100;
        annualTaxes = totalEstimatedAmount * annualTaxRate;
      } else if (payload.annualPropertyTaxes >= 100) {
        // property tax >= 100 means a whole dollar amount
        annualTaxes = payload.annualPropertyTaxes;
      }

      monthlyTaxes = round(annualTaxes / 12, 2);

      let annualHomeOwnersInsurance = (totalEstimatedAmount / 1000.0) * 3.5;

      if (payload.annualOwnersInsurance === 0) {
        // use the default tax rate because we didn't get anything
        annualHomeOwnersInsurance = 0;
      } else if (payload.annualOwnersInsurance > 0) {
        annualHomeOwnersInsurance = payload.annualOwnersInsurance;
      }

      monthlyInsurance = annualHomeOwnersInsurance / 12;
    }

    let adjustedPayment = payload.monthlyPayments - monthlyTaxes - monthlyInsurance;
    let adjustAmount = 0;
    if (!isNil(payload.monthlyPayments)) {
      adjustAmount = round(
        (adjustedPayment / monthlyInterestRate) * (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments)),
        2
      );
    }

    let finalAmount = adjustAmount + downPayment;

    const percentCalculated = adjustAmount / finalAmount;

    let pmiMonthly = 0;
    if (!isNil(payload.monthlyPayments) && percentCalculated > 0.8) {
      const percentOfMaxLTV = ((percentCalculated - 0.8) * 100) / 20; // 20 is the range from 80% LTV to 100% LTV
      const pmiRate = Math.max(0.3, 1.15 * percentOfMaxLTV) / 100.0; // from looking online, the PMI is roughly .3% up to 1.15%
      const pmiAnnual = totalEstimatedAmount * pmiRate;

      pmiMonthly = round(pmiAnnual / 12, 2);

      adjustedPayment -= pmiMonthly;
      adjustAmount = round(
        (adjustedPayment / monthlyInterestRate) * (1 - Math.pow(1 + monthlyInterestRate, -numberOfPayments)),
        2
      );
    }

    finalAmount = round(adjustAmount + downPayment, 2);

    return {
      ...state,
      affordabilityBuyerName: payload.buyerName || '',
      affordabilityDownPayment: round(downPayment, 2),
      affordabilityInterestRate: payload.interestRate,
      affordabilityMonthlyHomeownersInsurance: round(monthlyInsurance, 2),
      affordabilityMonthlyPayment: round(adjustedPayment + monthlyInsurance + monthlyTaxes + pmiMonthly, 2),
      affordabilityMortgageAmount: Math.abs(adjustAmount),
      affordabilityMortgageTerm: state.affordabilityMortgageTerm,
      affordabilityPrincipalAndInterest: Math.abs(round(adjustedPayment, 2)),
      affordabilityPrivateMortgageInsurance: pmiMonthly,
      affordabilityPropertyTax: monthlyTaxes,
      affordabilityPurchasePrice: Math.abs(finalAmount)
    };
  }),
  on(fromCalculatorActions.updateAmortizationMortgageTerm, (state, { payload }) => ({
    ...state,
    amortizationMortgageTerm: payload.mortgageTerm
  })),
  on(fromCalculatorActions.updateAmortizationCalculatorData, (state, { payload }) => ({
    ...state,
    amortizationAmount: payload.amount,
    amortizationInterestRate: payload.interestRate
  })),
  on(fromCalculatorActions.getAmortizationDataSuccess, (state, { payload }) => ({
    ...state,
    amortizationReportData: payload.reportData
  }))
);
